/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { GlobalFactStore } from './facts/store';

import type { Facts } from './index';

/**
 * Page-wide evaluation epoch. An epoch begins at each auction, in
 * Page.initialiseAuction, and eligibility results and fact snapshots are
 * memoised for its duration, collapsing that auction's repeated sweeps into
 * a single evaluation pass. Activity- and fact-driven invalidation still
 * applies within an epoch.
 *
 * Evaluation outside an auction (post-auction targeting, the select-response
 * callback, a deferred page-view record) deliberately reuses the current
 * auction's epoch, so what those paths see matches the select payload.
 */
class EvaluationContext {
  private static instance: EvaluationContext;
  private _epoch: number = 1;
  private globalFacts: Facts | null = null;

  private constructor() {}

  public static getInstance(): EvaluationContext {
    if (!EvaluationContext.instance) {
      EvaluationContext.instance = new EvaluationContext();
    }
    return EvaluationContext.instance;
  }

  public get epoch(): number {
    return this._epoch;
  }

  public beginEpoch(): void {
    this._epoch++;
    this.globalFacts = null;
  }

  /**
   * The global + page fact snapshot for the current epoch, assembled on
   * first access so it observes fact-store updates made earlier in the same
   * entry-point invocation.
   */
  public getGlobalFacts(): Facts {
    this.globalFacts ??= GlobalFactStore.getInstance().getFacts();
    return this.globalFacts;
  }

  public _reset(): void {
    this._epoch = 1;
    this.globalFacts = null;
  }
}

export { EvaluationContext };
